import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { histogram, quartiles } from "../data/analytics";
import { AGE_GROUPS, CHART_COLORS } from "../data/constants";
import { formatDuration, formatNumber, median } from "../data/formatters";
import type { NormalizedRunner } from "../types";
import { Chart, chartGridColor, chartTextStyle } from "../components/Chart";
import { MetricCard } from "../components/MetricCard";
import { Panel } from "../components/Panel";

function histogramOption(data: ReturnType<typeof histogram>, unit: string, color: string): EChartsOption {
  return {
    textStyle: chartTextStyle,
    tooltip: { trigger: "axis", formatter: (params: unknown) => {
      const item = (params as Array<{ dataIndex: number; value: number }>)[0];
      const bin = data[item.dataIndex];
      return `${bin.start.toFixed(1)}–${bin.end.toFixed(1)} ${unit}<br/>${item.value.toLocaleString()} 人`;
    } },
    grid: { left: 58, right: 18, top: 22, bottom: 56 },
    xAxis: { type: "category", name: unit, data: data.map((bin) => bin.start.toFixed(1)), axisLabel: { ...chartTextStyle, rotate: 30 }, axisLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "人數", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: [{ type: "bar", data: data.map((bin) => bin.count), itemStyle: { color } }],
  };
}

export function ActivityPage({ runners }: { runners: NormalizedRunner[] }) {
  const timeMedian = median(runners.map((runner) => runner.totalTimeSeconds));
  const activityMedian = median(runners.map((runner) => runner.activityCount));
  const timeBins = useMemo(() => histogram(runners.map((runner) => runner.totalTimeSeconds / 3600), 14), [runners]);
  const activityBins = useMemo(() => histogram(runners.map((runner) => runner.activityCount), 14), [runners]);
  const scatterData = useMemo(() => {
    const step = Math.max(1, Math.ceil(runners.length / 3500));
    return runners.filter((_, index) => index % step === 0).map((runner) => [runner.activityCount, +(runner.totalTimeSeconds / 3600).toFixed(2), runner.nickname]);
  }, [runners]);
  const boxRows = useMemo(() => AGE_GROUPS.map((group) => ({
    group,
    time: quartiles(runners.filter((runner) => runner.ageGroup === group).map((runner) => runner.totalTimeSeconds / 3600)),
    activities: quartiles(runners.filter((runner) => runner.ageGroup === group).map((runner) => runner.activityCount)),
  })), [runners]);

  const scatterOption: EChartsOption = useMemo(() => ({
    textStyle: chartTextStyle,
    tooltip: { formatter: (params: unknown) => {
      const value = (params as { value: [number, number, string] }).value;
      return `${value[2]}<br/>${value[0]} 次 / ${value[1]} 小時`;
    } },
    grid: { left: 62, right: 24, top: 20, bottom: 56 },
    xAxis: { type: "value", name: "跑步次數", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "累積小時", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: [{ type: "scatter", data: scatterData, symbol: "rect", symbolSize: 7, itemStyle: { color: CHART_COLORS[0], opacity: 0.52 } }],
  }), [scatterData]);

  const timeBoxOption: EChartsOption = useMemo(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "item" },
    grid: { left: 64, right: 20, top: 18, bottom: 82 },
    xAxis: { type: "category", data: boxRows.filter((row) => row.time).map((row) => row.group), axisLabel: { ...chartTextStyle, rotate: 38 }, axisLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "累積小時", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: [{ type: "boxplot", data: boxRows.filter((row) => row.time).map((row) => row.time!), itemStyle: { color: "rgba(255,207,92,.35)", borderColor: CHART_COLORS[1], borderWidth: 2 } }],
  }), [boxRows]);

  const activityBoxOption: EChartsOption = useMemo(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "item" },
    grid: { left: 64, right: 20, top: 18, bottom: 82 },
    xAxis: { type: "category", data: boxRows.filter((row) => row.activities).map((row) => row.group), axisLabel: { ...chartTextStyle, rotate: 38 }, axisLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "跑步次數", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: [{ type: "boxplot", data: boxRows.filter((row) => row.activities).map((row) => row.activities!), itemStyle: { color: "rgba(142,169,255,.35)", borderColor: CHART_COLORS[3], borderWidth: 2 } }],
  }), [boxRows]);

  const endpoints = useMemo(() => [...runners]
    .sort((a, b) => b.totalTimeSeconds - a.totalTimeSeconds || a.achievementOrder - b.achievementOrder)
    .slice(0, 8), [runners]);

  return (
    <div className="page-stack">
      <div className="metric-grid metric-grid--four">
        {/* 本頁共同母數 */}
        <MetricCard label="篩選後達標者" value={formatNumber(runners.length)} note="" tone="blue" />
        <MetricCard label="累積時間中位數" value={timeMedian == null ? "—" : formatDuration(timeMedian)} note="持續時間，不受時區影響" tone="mint" />
        <MetricCard label="跑步次數中位數" value={activityMedian == null ? "—" : `${activityMedian.toLocaleString()} 次`} note="篩選後達標者" tone="yellow" />
        <MetricCard label="散佈圖取樣" value={formatNumber(scatterData.length)} note={scatterData.length < runners.length ? "只影響繪圖，不影響統計" : "顯示全部樣本"} tone="pink" />
      </div>

      <div className="content-grid">
        <Panel title="累積活動時間分布" eyebrow="DURATION HISTOGRAM" aside={<span className="sample">單位：小時</span>}>
          {timeBins.length ? <Chart option={histogramOption(timeBins, "小時", CHART_COLORS[0])} label="累積活動時間直方圖" /> : <NoData />}
        </Panel>
        <Panel title="跑步次數分布" eyebrow="ACTIVITY HISTOGRAM" aside={<span className="sample">單位：次</span>}>
          {activityBins.length ? <Chart option={histogramOption(activityBins, "次", CHART_COLORS[1])} label="跑步次數直方圖" /> : <NoData />}
        </Panel>
      </div>

      <div className="content-grid">
        <Panel title="跑步次數 × 累積活動時間" eyebrow="SCATTER" aside={<span className="sample">N = {formatNumber(scatterData.length)}</span>}>
          {scatterData.length ? <Chart option={scatterOption} label="跑步次數與累積活動時間散佈圖" /> : <NoData />}
        </Panel>
        <Panel title="各年齡層時間箱形圖" eyebrow="BOX PLOT" aside={<span className="sample">最小／Q1／中位／Q3／最大</span>}>
          {runners.length ? <Chart option={timeBoxOption} label="各年齡層累積活動時間箱形圖" /> : <NoData />}
        </Panel>
      </div>

      <Panel title="各年齡層跑步次數箱形圖" eyebrow="ACTIVITY BOX PLOT" aside={<span className="sample">最小／Q1／中位／Q3／最大</span>}>
        {runners.length ? <Chart option={activityBoxOption} label="各年齡層跑步次數箱形圖" /> : <NoData />}
      </Panel>

      <Panel title="資料端點觀察" eyebrow="HIGH-END VALUES" aside={<span className="sample">僅列示，不判定異常或作弊</span>}>
        <div className="endpoint-list">
          {endpoints.map((runner) => (
            <article key={runner.recordKey}>
              <span>#{runner.achievementOrder}</span>
              <strong>{runner.nickname}</strong>
              <dl><div><dt>累積活動時間</dt><dd>{formatDuration(runner.totalTimeSeconds)}</dd></div><div><dt>跑步次數</dt><dd>{runner.activityCount} 次</dd></div></dl>
            </article>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function NoData() {
  return <div className="empty-state"><span>×</span><p>目前篩選條件沒有資料。</p></div>;
}
